import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Loader2, RefreshCw, Radar } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { INTEL_API, errMsg, OutreachSendButton } from "../components/intelligence/OutreachSendButton";
import { QuickCreate } from "../components/intelligence/QuickCreate";
import { IntelligenceTools } from "../components/intelligence/IntelligenceTools";
import { MorningDashboard } from "../components/intelligence/MorningDashboard";
import { MissionsPanel } from "../components/intelligence/MissionsPanel";
import { EditorialQueue } from "../components/intelligence/EditorialQueue";
import { HumanReviewQueue } from "../components/intelligence/HumanReviewQueue";
import { ActionCenter, AttentionBanner } from "../components/intelligence/ActionCenter";
import { PartnersPanel } from "../components/intelligence/PartnersPanel";
import { CrmData } from "../components/intelligence/CrmData";

const ALLOWED = ["admin", "super_admin"];
const ED_API = `${INTEL_API}/editorial`;

const Grid = ({ testid, cols, rows, render }) => (
  <Table data-testid={testid}>
    <TableHeader><TableRow>{cols.map((c) => <TableHead key={c}>{c}</TableHead>)}</TableRow></TableHeader>
    <TableBody>
      {rows.length === 0 && <TableRow><TableCell colSpan={cols.length} className="text-muted-foreground">Aucune donnée</TableCell></TableRow>}
      {rows.map(render)}
    </TableBody>
  </Table>
);

export default function IntelligencePage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(0);
  const allowed = ALLOWED.includes(user?.role);
  const bump = () => setTick((t) => t + 1);
  const [tab, setTab] = useState("todo");
  const [sub, setSub] = useState({ editorial: "queue", partners: "partner-mails", more: "dashboard" });
  const [filter, setFilter] = useState("");
  const [attn, setAttn] = useState(null);
  // Navigation par les tuiles / la bannière : ouvre directement « À traiter » (filtré) ou la file concernée
  const navigate = ({ tab: t, filter: f, sub: s2 }) => { setTab(t); if (f !== undefined) setFilter(f || ""); if (s2) setSub((x) => ({ ...x, [t]: s2 })); };
  const loadAttn = useCallback(async () => { try { setAttn((await axios.get(`${ED_API}/action-center/summary`)).data); } catch (e) { /* bannière non bloquante */ } }, []);
  useEffect(() => { if (allowed) { loadAttn(); const t = setInterval(loadAttn, 60000); return () => clearInterval(t); } return undefined; }, [allowed, loadAttn, tick]);

  const load = useCallback(async () => {
    try {
      const keys = ["summary", "organizations", "opportunities", "watchlists", "autopilot/events", "editorial-drafts", "outreach-actions", "audit"];
      const res = await Promise.all(keys.map((k) => axios.get(`${INTEL_API}/${k}`)));
      setData(Object.fromEntries(keys.map((k, i) => [k, res[i].data])));
    } catch (e) { toast.error(errMsg(e)); }
  }, []);

  useEffect(() => { if (allowed) load(); }, [allowed, load]);

  const act = async (path, body, msg) => {
    try { await axios.post(`${INTEL_API}/${path}`, body || {}); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  if (!allowed) return <div className="p-8 text-sm" data-testid="intel-access-denied">Accès réservé aux administrateurs.</div>;
  if (!data) return <div className="p-8" data-testid="intel-loading"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="intelligence-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><Radar className="w-6 h-6" /> ITERCATUS Intelligence</h1>
          <p className="text-sm text-muted-foreground">Partenaires, opportunités et radar éditorial. Aucun envoi automatique.</p>
        </div>
        <Button variant="outline" onClick={load} data-testid="intel-refresh-btn"><RefreshCw className="w-4 h-4 mr-2" />Actualiser</Button>
      </div>
      <AttentionBanner summary={attn} onOpen={() => navigate({ tab: "todo", filter: "" })} />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-auto w-full justify-start overflow-x-auto sm:flex-wrap" data-testid="intel-main-tabs">
          <TabsTrigger value="todo" data-testid="intel-tab-todo">À traiter{attn?.badge ? <Badge className="ml-2 bg-red-600 text-white" data-testid="intel-todo-badge">{attn.badge}</Badge> : null}</TabsTrigger>
          <TabsTrigger value="editorial" data-testid="intel-tab-editorial">Éditorial</TabsTrigger>
          <TabsTrigger value="partners" data-testid="intel-tab-partners">Partenaires</TabsTrigger>
          <TabsTrigger value="missions" data-testid="intel-tab-missions">Missions</TabsTrigger>
          <TabsTrigger value="more" data-testid="intel-tab-more">Plus</TabsTrigger>
        </TabsList>
        <TabsContent value="todo">
          <ActionCenter initialFilter={filter} refreshKey={tick} onChange={bump} onOpenMail={() => navigate({ tab: "partners", sub: "partner-mails" })} />
        </TabsContent>
        <TabsContent value="editorial">
          <Tabs value={sub.editorial} onValueChange={(v) => setSub((x) => ({ ...x, editorial: v }))}>
            <TabsList className="flex-wrap h-auto">
              <TabsTrigger value="queue" data-testid="intel-tab-queue">File éditoriale</TabsTrigger>
              <TabsTrigger value="drafts" data-testid="intel-tab-drafts">Brouillons</TabsTrigger>
              <TabsTrigger value="review" data-testid="intel-tab-review">Validations (Human Review)</TabsTrigger>
            </TabsList>
            <TabsContent value="queue"><EditorialQueue isSuper={user?.role === "super_admin"} onChange={bump} /></TabsContent>
            <TabsContent value="drafts">
          <Grid testid="intel-drafts-table" cols={["Titre", "Type", "Statut", "Article"]} rows={data["editorial-drafts"]} render={(d) => (
            <TableRow key={d.draft_id}><TableCell>{d.title}</TableCell><TableCell>{d.draft_type}</TableCell><TableCell>{d.status}</TableCell>
              <TableCell>{d.article_id
                ? <Link className="underline text-sm" to="/dashboard/articles" data-testid={`intel-draft-article-link-${d.draft_id}`}>À relire (articles)</Link>
                : <Button size="sm" onClick={() => act(`editorial-drafts/${d.draft_id}/to-article`, null, "Article créé (à relire)")} data-testid={`intel-draft-to-article-${d.draft_id}`}>Verser dans Articles</Button>}
              </TableCell></TableRow>)} />
            </TabsContent>
            <TabsContent value="review"><HumanReviewQueue onChange={bump} /></TabsContent>
          </Tabs>
        </TabsContent>
        <TabsContent value="partners">
          <PartnersPanel opportunities={data.opportunities} initialSub={sub.partners} onChange={() => { load(); bump(); }}
            outreachGrid={<>
          <Grid testid="intel-outreach-table" cols={["Objet", "Politique", "Approbation", "Envoi", "Actions"]} rows={data["outreach-actions"]} render={(a) => (
            <TableRow key={a.action_id}><TableCell>{a.subject}</TableCell><TableCell>{a.autonomy_policy}</TableCell><TableCell>{a.approval_status}</TableCell>
              <TableCell data-testid={`intel-outreach-delivery-${a.action_id}`}>{a.delivery?.status || "NOT_SENT"}</TableCell>
              <TableCell className="space-x-2">
                <Button size="sm" variant="ghost" onClick={() => act(`outreach-actions/${a.action_id}/approve`, null, "Approuvé pour action externe")} data-testid={`intel-outreach-approve-${a.action_id}`}>Approuver</Button>
                <OutreachSendButton action={a} onDone={load} />
              </TableCell></TableRow>)} />
            </>} />
        </TabsContent>
        <TabsContent value="missions"><MissionsPanel onChange={bump} /></TabsContent>
        <TabsContent value="more">
          <Tabs value={sub.more} onValueChange={(v) => setSub((x) => ({ ...x, more: v }))}>
            <TabsList className="flex-wrap h-auto">
              <TabsTrigger value="dashboard" data-testid="intel-tab-dashboard">Tableau de bord / activité</TabsTrigger>
              <TabsTrigger value="autopilot" data-testid="intel-tab-autopilot">Autopilot</TabsTrigger>
              <TabsTrigger value="audit" data-testid="intel-tab-audit">Journal</TabsTrigger>
              <TabsTrigger value="tools" data-testid="intel-tab-tools">Recherche & outils</TabsTrigger>
              <TabsTrigger value="crm" data-testid="intel-tab-crm">Données CRM (technique)</TabsTrigger>
            </TabsList>
            <TabsContent value="dashboard"><MorningDashboard refreshKey={tick} onNavigate={navigate} /></TabsContent>
            <TabsContent value="crm"><CrmData watchlists={data.watchlists} onChange={() => { load(); bump(); }} /></TabsContent>
            <TabsContent value="autopilot">
          <Grid testid="intel-events-table" cols={["Événement", "État", "Organisation", ""]} rows={data["autopilot/events"]} render={(e) => (
            <TableRow key={e.event_id}><TableCell>{e.opportunity?.title}</TableCell><TableCell><Badge>{e.state}</Badge></TableCell><TableCell>{e.organization?.name}</TableCell>
              <TableCell className="space-x-2">
                <Button size="sm" variant="ghost" onClick={() => act(`autopilot/events/${e.event_id}/approve`, null, "Approuvé")} data-testid={`intel-event-approve-${e.event_id}`}>Approuver</Button>
                <Button size="sm" variant="ghost" onClick={() => act(`autopilot/events/${e.event_id}/dismiss`, null, "Écarté")} data-testid={`intel-event-dismiss-${e.event_id}`}>Écarter</Button>
              </TableCell></TableRow>)} />
            </TabsContent>
            <TabsContent value="audit">
          <Grid testid="intel-audit-table" cols={["Date", "Acteur", "Action", "Cible"]} rows={data.audit} render={(r) => (
            <TableRow key={r.id}><TableCell className="text-xs">{r.ts}</TableCell><TableCell>{r.actor}</TableCell><TableCell>{r.action}</TableCell><TableCell className="text-xs">{r.target_type} {r.target_id}</TableCell></TableRow>)} />
        </TabsContent>
            <TabsContent value="tools"><QuickCreate onCreated={load} /><IntelligenceTools organizations={data.organizations} onChange={load} /></TabsContent>
          </Tabs>
        </TabsContent>
      </Tabs>
    </div>
  );
}
